import { useState, useEffect } from 'react';

import { Button, Accordion, AccordionItem } from 'carbon-components-react';
import Helmet from 'react-helmet';
import './PAROI.scss';
import 'src/common/style/cds-data.scss';
import 'src/common/style/importantStyle.scss';
import 'src/common/style/common.scss';
import { segmentTracking } from '../lib/tracker';
import Thanks from './Thanks';
import { useEnv } from 'src/common/hooks/widgets/useEnv';
import { useBPName } from 'src/common/hooks/widgets/useBPName';
import * as URX_FORM from '../constants/urx-form';
import { swapCountryAndLanguage } from 'src/common/utils/swapCountryAndLanguage';
import {
  defaultAnnual,
  defaultDepartments,
  defaultParticipants,
  UT30RPAROI,
} from '../constants/defaults';
import messages from '../locales/messages';
import { IBMLocale } from 'src/common/locale/mapValidLocale';
import { LangDefaults, useLang } from 'src/common/hooks/widgets/useLang';
import countryList from '../data/currencies.json';
import {
  urxScriptLoader,
  urxScript,
} from 'src/common/hooks/widgets/globalDefinitions';
import Parameter from './parameter';
import DynamicsBar from './dynamicBar';
import { initUrxForm } from 'src/common/hooks/widgets/urxForm';
import { FormModal } from 'src/common/utils/FormModal';
import Cookies from 'js-cookie';
import { useCookiesURL } from 'src/common/hooks/widgets/useCookiesURL';
import { createUseTranslation } from '../../../common/hooks/widgets/useTranslation';
import { usePAROISelector } from '../lib/redux/hooks';

export interface PAROIEstimatorProps {
  langCode: string;
  test: boolean;
  countryToTest: string;
}

const PAROI = ({ langCode, test, countryToTest }: PAROIEstimatorProps) => {
  const localeCode = swapCountryAndLanguage(langCode as IBMLocale);
  const [annualSpend, setAnnualSpend] = useState(defaultAnnual);
  const [numberDepartment, setNumberDepartment] = useState(defaultDepartments);
  const [numberParticipants, setNumberParticipants] =
    useState(defaultParticipants);
  const [calculate, setCalculate] = useState(false);
  const [applyFormat, setApplyFormat] = useState(false);
  const [environment] = useEnv();
  const [urxlang] = useState(localeCode.toString());
  const [, setCurrency] = useState('');
  const [, setCountry] = useState('');
  const [closeModal, setCloseModal] = useState(false);

  const [lang] = useLang(
    countryList,
    test,
    countryToTest,
    setCurrency,
    setCountry,
    localeCode,
  );

  const [formComplete, setFormComplete] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { bpName, companyId, logo } = useBPName(UT30RPAROI, true);
  const cookieURL = useCookiesURL();
  const [result, setResult] = useState('simple' as string);
  const AEMMessages = usePAROISelector((state) => state.translations.messages);
  const useTranslation = createUseTranslation(messages, AEMMessages);

  let localStorageFound = localStorage.getItem('langLocale');
  if (localStorageFound == null)
    localStorageFound = LangDefaults.localCountryLang;

  const getBp = () => {
    if (bpName) return bpName;
    const bpField = document.querySelector<HTMLInputElement>('#Q_BPNAME');
    return bpField ? bpField.value : '';
  };
  useEffect(() => {
    if (calculate) {
      setCookie();
    }
  }, [annualSpend, numberDepartment, numberParticipants, bpName, calculate]);

  const setCookie = () => {
    Cookies.set(
      'urxdcq',
      JSON.stringify({
        source: URX_FORM.FORM_ID,
        // One field to provide all values (stringify)
        dcq: {
          Q_NOTESDATA:
            cookieURL +
            'Annual spend:' +
            annualSpend +
            ' | Number of departments: ' +
            numberDepartment +
            ' | Number of participants: ' +
            numberParticipants +
            ' | ROI estimated: ' +
            result +
            (getBp() ? ` | Preferred BP: ${getBp()}` : ''), // BP Name
        },
      }),
    );
    console.log(Cookies.get('urxdcq'));
  };

  window.urxEnvironment = environment;
  window.onUrxFormSubmitSuccess = () => {
    setFormComplete(!formComplete);
    setDrawerOpen(false); // Hide Drawer on Thank you page
    setCloseModal(true);
  };

  return (
    <section id="PA_ROI" className="widget-styles">
      <div
        className="PA_ROI dsh-widget widget-reset"
        dir={localeCode.includes('ar') ? 'rtl' : 'ltr'}
      >
        <Helmet>
          <script type="application/javascript" src={urxScriptLoader}></script>
          <script type="application/javascript" src={urxScript}></script>
        </Helmet>
        {formComplete ? (
          <Thanks
            annualSpend={annualSpend}
            numberDepartment={numberDepartment}
            numberParticipants={numberParticipants}
            bpName={bpName}
            bpConsentMail={false}
          ></Thanks>
        ) : (
          // Not Form completed
          <div className="bx--grid" id="PAROIGrid">
            <div className="bx--row">
              <div className="bx--col-lg-16 bx--col-md-16 bx--col-sm-4">
                <div className="bx--row">
                  <div className="bx--col-lg-9 bx--col-md-16 bx--col-sm-16">
                    {/*  Parameters */}
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-12 bx--col-sm-4 content">
                        {/*  Parameter 1*/}
                        <Parameter
                          captionClassName="page_body"
                          captionText={
                            useTranslation('annual_spend', true) as string
                          }
                          textId="annual_spend"
                          labText=""
                          defValue=""
                          setValue={setAnnualSpend}
                          prefix="$"
                          posfix="K"
                          applyFormat={applyFormat}
                          setApplyFormat={setApplyFormat}
                          fieldName="Annual Spend"
                        />
                        {/*  Parameter 2*/}
                        <Parameter
                          captionClassName="page_body"
                          captionText={
                            useTranslation('number_department', true) as string
                          }
                          textId="number_department"
                          labText=""
                          defValue=""
                          setValue={setNumberDepartment}
                          prefix=""
                          posfix=""
                          applyFormat={null}
                          setApplyFormat={null}
                          fieldName="Number of departments"
                        />
                        {/*  Parameter 3*/}
                        <Parameter
                          captionClassName="page_body"
                          captionText={
                            useTranslation(
                              'number_participants',
                              true,
                            ) as string
                          }
                          textId="number_participants"
                          labText=""
                          defValue=""
                          setValue={setNumberParticipants}
                          prefix=""
                          posfix=""
                          applyFormat={null}
                          setApplyFormat={null}
                          fieldName="Number of participants"
                        />
                      </div>
                    </div>
                    {/* CTA */}
                    <div className="bx--row cta">
                      <div className="bx--col-lg-6 bx--col-md-4 bx--col-sm-4 content">
                        <Button
                          onClick={() => {
                            segmentTracking('CTA Clicked', {
                              pageTitle: 'PA ROI Pricing Page',
                              object: 'Pricing Page',
                              resultValue: '',
                              CTA: `Calculate ROI button`,
                              location: 'ROI Estimator',
                              action: 'CTA clicked',
                              field: '',
                            });
                            setCalculate(true);
                            setApplyFormat(true);
                          }}
                        >
                          {useTranslation('cta_ROI')}
                        </Button>
                      </div>
                    </div>
                    {/*  footnotes */}
                    <div className="bx--row">
                      <div className="bx--col-lg-16 bx--col-md-12 bx--col-sm-4">
                        <Accordion size="sm">
                          <AccordionItem
                            onClick={() => {
                              segmentTracking('CTA Clicked', {
                                pageTitle: 'PA ROI Pricing Page',
                                object: 'Pricing Page',
                                resultValue: '',
                                CTA: `Disclaimer text`,
                                location: 'ROI Estimator',
                                action: 'CTA clicked',
                                field: '',
                              });
                            }}
                            title={
                              <span className="disclaimer">
                                {useTranslation('footnotes_title', true)}
                              </span>
                            }
                            className="disclaimer"
                          >
                            <p className="disclaimer_body">
                              {useTranslation('footnotes_paragraph')}
                            </p>
                          </AccordionItem>
                        </Accordion>
                      </div>
                    </div>
                  </div>
                  {/* Start display results  */}
                  <div className="bx--col-lg-4 bx--col-md-16 bx--col-sm-16">
                    <div className="bx--row" id="stickyNav">
                      <div className="RPA_ROI__sticky-nav price_table_comp">
                        {/* Graph Title */}
                        <div className="bx-row">
                          <div className="bx--col-lg-16 bx--vol-md-12 bx--col-sm-4 graph_title">
                            <p>{useTranslation('graph_title')}</p>
                          </div>
                        </div>
                        {/* Graph */}
                        <div className="bx--row">
                          <div className="bx--col-lg-16 bx--vol-md-12 bx--col-sm-4">
                            <DynamicsBar
                              topText={
                                useTranslation('top_text', true) as string
                              }
                              bottonText={
                                useTranslation('bottom_text', true) as string
                              }
                              annual_spend={annualSpend}
                              number_department={numberDepartment}
                              number_participants={numberParticipants}
                              calculate={calculate}
                              setCalculate={setCalculate}
                              setResult={setResult}
                            />
                          </div>
                        </div>
                        {/* CTA */}
                        <div id="RPA_ROI.price_toggle" className="mar4">
                          <Button
                            id="request_btn"
                            onClick={() => {
                              segmentTracking('CTA Clicked', {
                                pageTitle: 'PA ROI Pricing Page',
                                object: 'Pricing Page',
                                resultValue: 'CTA clicked',
                                CTA: 'Request a quote now',
                                location: 'ROI Estimator',
                                action: '',
                                field: '',
                              });
                              initUrxForm(
                                environment,
                                URX_FORM.INSTANCE_ID,
                                URX_FORM.FORM_ID,
                                urxlang as IBMLocale,
                                companyId,
                              );
                              setDrawerOpen(true);
                            }}
                          >
                            {useTranslation('cta_contact')}
                          </Button>
                        </div>
                        {/* End display results */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <FormModal
          pageTitle="Planning Analytics ROI Page"
          object="Planning Analytics ROI Page"
          productTitle={'Planning Analytics ROI'}
          pageUrl={''}
          location={'Estimator'}
          bpName={bpName}
          logo={logo}
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          setCloseModal={setCloseModal}
          closeModal={closeModal}
          formId={URX_FORM.FORM_ID}
          id={URX_FORM.INSTANCE_ID}
          lang={lang}
        ></FormModal>
      </div>
    </section>
  );
};

export default PAROI;
